import { useQuery } from "@apollo/client";
import moment from "moment";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import {
  GetMeetingNewPageQueryQuery,
  GetMeetingNewPageQueryQueryVariables,
  GetProfileOneononesQueryQuery,
  GetProfileOneononesQueryQueryVariables,
} from "types/graphql-schema";

import { MeetingPageContent } from "@apps/meeting-new/components/meeting-page";
import { MeetingPageContext } from "@apps/meeting-new/components/meeting-page-context";
import MeetingPageTOC from "@apps/meeting-new/components/meeting-page-toc";
import {
  TocMeeting,
  meetingRowDomId,
  useActiveMeetingId,
} from "@apps/meeting-new/components/meeting-page-toc-helpers";
import { MeetingTOCDockContext } from "@apps/meeting-new/components/meeting-toc-dock-context";
import getMeetingQuery from "@apps/meeting-new/graphql/get-meeting-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { removeCurrentYear } from "@helpers/helpers";

import getProfileOneononesQuery from "./graphql/get-profile-oneonones-query";
import OneononeMeetingEmptyCards from "./oneonone-meeting-empty-cards";

type PreviousMeetingInfo = {
  id: number;
  startDatetime: string | null;
} | null;

const formatLoadPreviousDate = (startDatetime: string | null): string => {
  if (!startDatetime) return "";
  const t = moment(startDatetime);
  const timeFormat = t.minute() === 0 ? "ha" : "h:mma";
  return t.format(`MMM D, ${timeFormat}`);
};

// One meeting in the list: fetches the meeting and renders the meeting-page
// content (details, title/date, prev-next arrows and topics) in normal flow.
// We render MeetingPageContent rather than the full MeetingPage so several can
// stack in the list (the full page uses absolute-positioned layout chrome).
const OneononeRow = ({
  meetingId,
  subjectId,
  onNavigate,
  animateTopicLayout,
  scrollOnLoad,
  onScrolled,
  onLoaded,
  placeholderDate,
  loadingMeetingId,
}: {
  meetingId: number;
  // The profile user: the prev/next arrows page through the 1-on-1s where this
  // person is the subject, across every MeetingGroup of the relationship.
  subjectId: number;
  loadingMeetingId: number | null;
  onNavigate: (
    target: { id: number; meetingGroupId: number },
    position: "before" | "after",
  ) => void;
  animateTopicLayout: boolean;
  // True when this row is the target of a pagination navigation and should
  // scroll itself into view once its meeting has loaded.
  scrollOnLoad: boolean;
  onScrolled: () => void;
  // Called once the meeting has loaded; receives the meeting itself (for the
  // TOC) plus the previous meeting info (id + startDatetime, or null) so the
  // parent can render the "Load previous meeting" button at the bottom of the
  // list.
  onLoaded?: (info: {
    meeting: TocMeeting;
    previousMeeting: PreviousMeetingInfo;
  }) => void;
  // Pre-known meeting date for the loading placeholder.
  placeholderDate?: string | null;
}) => {
  // Sidebar state lives in ProfilePage; we read it via the dock context so we
  // can propagate it to MeetingPageContext for the inline header chevrons.
  const { sidebarVisible } = useContext(MeetingTOCDockContext);
  const { data, loading } = useQuery<
    GetMeetingNewPageQueryQuery,
    GetMeetingNewPageQueryQueryVariables
  >(getMeetingQuery, { variables: { meetingId } });
  const meeting = data?.meeting;
  const rowRef = useRef<HTMLDivElement | null>(null);
  const isLoaded = !loading && !!meeting && !!meeting.meetingGroup;

  // Scroll into view only once the meeting content has loaded. Scrolling while
  // the loading placeholder is showing would land on the spinner and then jump
  // as the (differently sized) content swaps in.
  useEffect(() => {
    if (scrollOnLoad && isLoaded && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      onScrolled();
      // Editors / images can shift heights mid-animation; re-fire once the
      // smooth scroll has typically completed to correct any drift.
      const t = window.setTimeout(() => {
        rowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 700);
      return () => window.clearTimeout(t);
    }
  }, [scrollOnLoad, isLoaded, onScrolled]);

  const previousMeeting = meeting?.previousMeeting ?? null;
  useEffect(() => {
    if (isLoaded && meeting) {
      onLoaded?.({
        meeting: {
          id: meeting.id,
          startDatetime: meeting.startDatetime ?? null,
          meetingGroupId: meeting.meetingGroupId,
        },
        previousMeeting: previousMeeting
          ? {
              id: previousMeeting.id,
              startDatetime: previousMeeting.startDatetime ?? null,
            }
          : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, previousMeeting?.id]);

  return (
    // min-h-[85vh] ensures each 1-on-1 fills the majority of the viewport so
    // that the next (older) 1-on-1's date header just peeks in at the bottom.
    <div
      ref={rowRef}
      id={meetingRowDomId(meetingId)}
      className="js-test-oneonone-row scroll-mt-4 min-h-[85vh]"
    >
      {loading || !meeting || !meeting.meetingGroup ? (
        // Reserve roughly a meeting's height so swapping in the loaded content
        // doesn't shift the layout / scroll position.
        <div className="flex min-h-[290px] items-center justify-center">
          <div className="flex items-center gap-2 bg-gray-100 rounded-md p-2">
            <Loading mini size="4" />
            <span className="text-sm text-gray-700">
              {placeholderDate
                ? `Loading meeting: ${removeCurrentYear(
                    moment(placeholderDate).format("ddd, ll"),
                  )}`
                : "Loading meeting…"}
            </span>
          </div>
        </div>
      ) : (
        <MeetingPageContext.Provider
          value={{
            isInSidebar: false,
            isInExtension: false,
            autoFocusDefaultTopic: false,
            animateTopicLayout,
            onNavigateToMeeting: onNavigate,
            oneononeSubjectId: subjectId,
            loadingMeetingId,
            meetingSidebarVisible: sidebarVisible,
          }}
        >
          <MeetingPageContent
            meeting={meeting}
            meetingGroupId={meeting.meetingGroup.id}
          />
        </MeetingPageContext.Provider>
      )}
    </div>
  );
};

const ProfileOneonone = ({
  profileId,
  profileFirstName,
  profileEmail,
  onEmptyChange,
}: {
  profileId: number;
  profileFirstName: string | null | undefined;
  profileEmail: string;
  onEmptyChange: (isEmpty: boolean) => void;
}) => {
  const label = useLabel();
  const link = useLink();
  const organization = currentOrganizationVar();
  // The TOC renders into a dock element owned by ProfilePage (a sibling of
  // the profile sidebar). See MeetingTOCDockContext.
  const { dockEl: tocDock } = useContext(MeetingTOCDockContext);
  const { meetingId: meetingIdStr } = useParams<{ meetingId?: string }>();
  const parsedMeetingId = meetingIdStr
    ? Number.parseInt(meetingIdStr, 10)
    : null;
  const meetingIdParam =
    parsedMeetingId !== null && Number.isFinite(parsedMeetingId)
      ? parsedMeetingId
      : null;

  // The ordered list of meetings shown in the tab (newest at the top). The
  // prev/next arrows on each embedded meeting page paginate this list: an
  // older meeting is inserted below ("after"), a newer one above ("before").
  const [displayedIds, setDisplayedIds] = useState<number[]>(
    meetingIdParam !== null ? [meetingIdParam] : [],
  );
  const [pendingScrollId, setPendingScrollId] = useState<number | null>(null);
  // Captured per row when it loads; the entry for the bottom-most row drives
  // the "Load previous meeting: <date>" button below the list.
  const [previousMeetingByRow, setPreviousMeetingByRow] = useState<
    Record<number, PreviousMeetingInfo>
  >({});
  // Each row's own meeting info, captured when the row finishes loading,
  // so the TOC can render a line button for each displayed meeting.
  const [meetingByRow, setMeetingByRow] = useState<Record<number, TocMeeting>>(
    {},
  );
  // Dates known before a row's GraphQL query resolves (TOC click target or
  // another row's previousMeeting field). Drives the "Loading meeting:
  // <date>" placeholder so users see *which* meeting is loading.
  const [placeholderDates, setPlaceholderDates] = useState<
    Record<number, string | null>
  >({});
  const recordPlaceholderDate = (id: number, date?: string | null) => {
    if (!date) return;
    setPlaceholderDates((prev) =>
      prev[id] === date ? prev : { ...prev, [id]: date },
    );
  };

  const handleRowLoaded = (
    rowMeetingId: number,
    info: { meeting: TocMeeting; previousMeeting: PreviousMeetingInfo },
  ) => {
    setPreviousMeetingByRow((prev) =>
      prev[rowMeetingId]?.id === info.previousMeeting?.id
        ? prev
        : { ...prev, [rowMeetingId]: info.previousMeeting },
    );
    setMeetingByRow((prev) =>
      prev[rowMeetingId]?.startDatetime === info.meeting.startDatetime
        ? prev
        : { ...prev, [rowMeetingId]: info.meeting },
    );
    if (info.previousMeeting?.startDatetime) {
      recordPlaceholderDate(
        info.previousMeeting.id,
        info.previousMeeting.startDatetime,
      );
    }
  };

  const { activeMeetingId, setActiveMeetingId } =
    useActiveMeetingId(displayedIds);

  const handleScrollToMeeting = (id: number) => {
    setActiveMeetingId(id);
    const scrollToRow = () => {
      const el = document.getElementById(meetingRowDomId(id));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    scrollToRow();
    // Editors / images can shift heights mid-animation; re-fire after the
    // smooth scroll typically completes so any drift is corrected.
    window.setTimeout(scrollToRow, 700);
  };

  // Load a meeting (clicked from the TOC) into the displayed list at the
  // right spot chronologically (newest first), then scroll to it.
  const handleLoadMeeting = (target: TocMeeting) => {
    setActiveMeetingId(target.id);
    recordPlaceholderDate(target.id, target.startDatetime);
    setDisplayedIds((prev) => {
      if (prev.includes(target.id)) return prev;
      const targetTime = target.startDatetime
        ? moment(target.startDatetime)
        : null;
      let insertIndex = prev.length;
      if (targetTime) {
        for (let i = 0; i < prev.length; i++) {
          const m = meetingByRow[prev[i]];
          if (m?.startDatetime && targetTime.isAfter(moment(m.startDatetime))) {
            insertIndex = i;
            break;
          }
        }
      }
      return [
        ...prev.slice(0, insertIndex),
        target.id,
        ...prev.slice(insertIndex),
      ];
    });
    link.replace(`/profile/${profileId}/oneonone/${target.id}`);
    setPendingScrollId(target.id);
  };

  const now = useMemo(() => moment().startOf("hour").format(), []);

  // Only needed to bootstrap the starting meeting when the tab is opened
  // without a specific meeting in the URL (the upcoming 1-on-1, or the most
  // recent past one). Adjacent meetings are then loaded by the meeting page's
  // own prev/next navigation.
  const { data, loading } = useQuery<
    GetProfileOneononesQueryQuery,
    GetProfileOneononesQueryQueryVariables
  >(getProfileOneononesQuery, {
    variables: {
      subject: profileId,
      now,
      beforeDate: now,
      includeUpcoming: true,
      upcomingFirst: 1,
      pastFirst: 1,
    },
    skip: !organization.featureFlags.meetings || meetingIdParam !== null,
  });

  // Default view (opening the tab): the upcoming 1-on-1 and the most recent
  // previous one.
  const upcomingId = data?.upcoming?.edges?.[0]?.node?.id ?? null;
  const pastId = data?.past?.edges?.[0]?.node?.id ?? null;
  const defaultIds = useMemo(
    () => [upcomingId, pastId].filter((id): id is number => id !== null),
    [upcomingId, pastId],
  );

  // When deep-linked to a meeting, fetch it so we know its start time.
  const { data: anchorData } = useQuery<
    GetMeetingNewPageQueryQuery,
    GetMeetingNewPageQueryQueryVariables
  >(getMeetingQuery, {
    variables: { meetingId: meetingIdParam ?? -1 },
    skip: meetingIdParam === null,
  });
  const anchorStartDatetime = anchorData?.meeting?.startDatetime ?? null;

  // The previous 1-on-1 to show below the deep-linked one must be the profile
  // user's previous 1-on-1 as the SUBJECT (across all MeetingGroups, including a
  // prior manager) -- not just the prior meeting sharing the same participants.
  // Same subject scoping as the list and the prev/next arrows.
  const { data: anchorPreviousData } = useQuery<
    GetProfileOneononesQueryQuery,
    GetProfileOneononesQueryQueryVariables
  >(getProfileOneononesQuery, {
    variables: {
      subject: profileId,
      now,
      beforeDate: anchorStartDatetime ?? now,
      includeUpcoming: false,
      upcomingFirst: 1,
      pastFirst: 1,
    },
    skip:
      !organization.featureFlags.meetings ||
      meetingIdParam === null ||
      anchorStartDatetime === null,
  });
  const anchorPreviousId =
    anchorPreviousData?.past?.edges?.[0]?.node?.id ?? null;

  // Bootstrap / reset the displayed list from the URL.
  useEffect(() => {
    if (meetingIdParam !== null) {
      // Re-bootstrap when the URL points at a meeting that isn't shown (e.g. a
      // fresh deep-link). Meetings reached via the pagination arrows are already
      // present, so this preserves the paginated list.
      if (!displayedIds.includes(meetingIdParam)) {
        setDisplayedIds([meetingIdParam]);
      } else if (
        displayedIds.length === 1 &&
        anchorPreviousId !== null &&
        anchorPreviousId !== meetingIdParam
      ) {
        // Deep-link: also show the previous 1-on-1 below, once the meeting has
        // loaded and we know its previous meeting.
        setDisplayedIds([meetingIdParam, anchorPreviousId]);
      }
      return;
    }
    // Bare /oneonone route (e.g. clicking the tab): show the upcoming + previous
    // 1-on-1, and reflect the focused meeting in the URL for the AskAI sidebar.
    if (
      defaultIds.length > 0 &&
      (displayedIds.length !== defaultIds.length ||
        displayedIds.some((id, i) => id !== defaultIds[i]))
    ) {
      setDisplayedIds(defaultIds);
      link.replace(`/profile/${profileId}/oneonone/${defaultIds[0]}`);
    }
  }, [
    meetingIdParam,
    anchorPreviousId,
    defaultIds,
    displayedIds,
    profileId,
    link,
  ]);

  const handleNavigate = (
    sourceId: number,
    target: { id: number; meetingGroupId: number },
    position: "before" | "after",
  ) => {
    setDisplayedIds((prev) => {
      if (prev.includes(target.id)) return prev;
      const idx = prev.indexOf(sourceId);
      const insertAt =
        idx === -1 ? prev.length : position === "after" ? idx + 1 : idx;
      return [...prev.slice(0, insertAt), target.id, ...prev.slice(insertAt)];
    });
    // Keep the URL/AskAI sidebar pointed at the meeting we navigated to.
    link.replace(`/profile/${profileId}/oneonone/${target.id}`);
    setPendingScrollId(target.id);
  };

  const bottomRowId = displayedIds[displayedIds.length - 1];
  const bottomRowPrevious = previousMeetingByRow[bottomRowId] ?? null;
  // Pending scroll target whose row data hasn't loaded yet — shared with
  // the TOC and inline chevrons so both show a spinner during fetch.
  const loadingMeetingId =
    pendingScrollId !== null && !meetingByRow[pendingScrollId]
      ? pendingScrollId
      : null;
  const canLoadAnotherPreviousMeeting =
    !!bottomRowPrevious && !displayedIds.includes(bottomRowPrevious.id);

  // Auto-load the next older 1-on-1 as soon as the user has scrolled into
  // the current bottommost row. The cascade pauses while the newly-added
  // row is still fetching (bottomRowPrevious is null until its data lands)
  // and resumes when the user scrolls into the new bottom.
  useEffect(() => {
    if (
      activeMeetingId !== null &&
      activeMeetingId === bottomRowId &&
      canLoadAnotherPreviousMeeting &&
      bottomRowPrevious
    ) {
      setDisplayedIds((prev) =>
        prev.includes(bottomRowPrevious.id)
          ? prev
          : [...prev, bottomRowPrevious.id],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeMeetingId,
    bottomRowId,
    canLoadAnotherPreviousMeeting,
    bottomRowPrevious?.id,
  ]);

  const isLoadingEmpty =
    organization.featureFlags.meetings &&
    meetingIdParam === null &&
    loading &&
    displayedIds.length === 0;
  const isEmpty = !isLoadingEmpty && displayedIds.length === 0;

  useEffect(() => {
    onEmptyChange(isEmpty);
    return () => onEmptyChange(false);
  }, [isEmpty, onEmptyChange]);

  if (isLoadingEmpty) {
    return (
      <Loading>Loading {label("oneonone", { titleCase: true })}s...</Loading>
    );
  }

  // No 1-on-1 exists for this profile yet → render the get-started cards
  // standalone (no meeting chrome). Picking "Create empty meeting" or
  // "Prepare with Topicflow AI" creates a draft and redirects to it; from
  // then on the user sees the regular meeting view (with cards already
  // dismissed for this profile).
  if (isEmpty) {
    return (
      <OneononeMeetingEmptyCards
        profileId={profileId}
        profileFirstName={profileFirstName}
        profileEmail={profileEmail}
      />
    );
  }

  const handleClickLoadPreviousMeeting = () => {
    if (!bottomRowPrevious) return;
    setDisplayedIds((prev) =>
      prev.includes(bottomRowPrevious.id)
        ? prev
        : [...prev, bottomRowPrevious.id],
    );
  };

  const displayedMeetings = displayedIds
    .map((id) => meetingByRow[id])
    .filter((m): m is TocMeeting => !!m);

  return (
    <div className="relative" aria-label="1-on-1 list">
      {/* The TOC's DOM lives inside the dock owned by ProfilePage (a sibling
          of the profile right-sidebar). Portal so the TOC keeps its data
          wired through props from this list, while its position is decided
          alongside the sidebar. */}
      {tocDock !== null &&
        displayedMeetings.length > 0 &&
        createPortal(
          <MeetingPageTOC
            displayedMeetings={displayedMeetings}
            activeMeetingId={activeMeetingId}
            onScrollToMeeting={handleScrollToMeeting}
            onLoadMeeting={handleLoadMeeting}
            loadingMeetingId={loadingMeetingId}
            subjectId={profileId}
          />,
          tocDock,
        )}
      <div className="flex flex-col pb-12">
        {displayedIds.map((id) => (
          <OneononeRow
            key={id}
            meetingId={id}
            subjectId={profileId}
            animateTopicLayout={displayedIds.length === 1}
            scrollOnLoad={id === pendingScrollId}
            onScrolled={() => setPendingScrollId(null)}
            onLoaded={(info) => handleRowLoaded(id, info)}
            placeholderDate={placeholderDates[id] ?? null}
            loadingMeetingId={loadingMeetingId}
            onNavigate={(target, position) =>
              handleNavigate(id, target, position)
            }
          />
        ))}
        {canLoadAnotherPreviousMeeting && (
          <div className="flex justify-center mt-8">
            <Button
              theme={buttonTheme.default}
              size={ButtonSize.medium}
              onClick={handleClickLoadPreviousMeeting}
            >
              Load previous meeting:{" "}
              {formatLoadPreviousDate(bottomRowPrevious.startDatetime)}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileOneonone;
